import React, { createContext, useContext, ReactNode, useState, useEffect } from "react";
import {
  AccountBalance,
  PaymentLimit,
  User,
  ToggleSwitch,
  v4Service,
  alfredService,
  isCxmExchange,
} from "src/api";
import { useUrlParamsContext } from "src/contexts";

interface DepositWithdrawContextType {
  user: User;
  paymentsLimits: PaymentLimit;
  balance: AccountBalance;
  widgetDataLoading: boolean;
  widgetDataError: string;
  /**
   * Pre-mount Alfred eligibility code from `GET /psp/alfred/eligibility`.
   * `null` when the user is eligible; otherwise one of:
   *   ALFRED_NOT_AVAILABLE_FOR_COUNTRY
   *   ALFRED_CUSTOMER_NOT_PROVISIONED
   *   ALFRED_KYC_PENDING
   *   ALFRED_KYC_REJECTED
   *   USER_NOT_VERIFIED
   * The deposit/withdrawal pages branch on this and render a
   * full-screen blocker instead of the form when it's set.
   */
  alfredIneligibilityCode: string | null;
}

const initialPaymentLimits: PaymentLimit = {
  currency_id: "",
  deposit_min_amount: 0,
  withdrawal_min_amount: 0,
};

const initialBalance: AccountBalance = {
  currency: {
    currency_id: "",
    precision: 2,
  },
  free_balance: 0,
  wallet_id: "",
};

const initialUser: User = {
  user_id: "",
  limit_group_id: "",
  mfa_status: ToggleSwitch.Off,
};

const DepositWithdrawContext = createContext<DepositWithdrawContextType | undefined>(undefined);

interface DepositWithdrawProviderProps {
  children: ReactNode;
}

export const DepositWithdrawProvider: React.FC<DepositWithdrawProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User>(initialUser);
  const [paymentsLimits, setPaymentsLimits] = useState<PaymentLimit>(initialPaymentLimits);
  const [balance, setBalance] = useState<AccountBalance>(initialBalance);
  const [widgetDataLoading, setWidgetDataLoading] = useState<boolean>(true);
  const [widgetDataError, setWidgetDataError] = useState<string>("");
  const [alfredIneligibilityCode, setAlfredIneligibilityCode] = useState<string | null>(null);
  const { currencyId, walletId, exchange } = useUrlParamsContext();

  useEffect(() => {
    const fetchWidgetData = async () => {
      try {
        const user = await v4Service.getUser({});

        const paymentsLimits = await v4Service.getPaymentsLimits({
          currency_id: currencyId,
          limit_group_id: user.limit_group_id,
        });

        const balance = await v4Service.getAccountsBalances({
          currency_id: currencyId,
          wallet_id: walletId || undefined,
        });

        setUser(user);
        setPaymentsLimits(paymentsLimits.length > 0 ? paymentsLimits[0] : initialPaymentLimits);
        setBalance(balance.length > 0 ? balance[0] : initialBalance);

        // Eligibility is best-effort and deliberately outside the bootstrap
        // path: a 5xx here must not make an otherwise-eligible user stare at
        // a spinner. It used to sit in the same try, so any failure killed
        // the whole page — which is what a tenant with no Alfred set up hits
        // every time. Skipped entirely on CXM tenants, where Alfred is not
        // configured and its verdict means nothing.
        if (!isCxmExchange(exchange)) {
          try {
            const eligibility = await alfredService.getEligibility();
            setAlfredIneligibilityCode(eligibility.eligible ? null : eligibility.code);
          } catch (eligibilityError) {
            console.warn("Alfred eligibility check failed:", eligibilityError);
          }
        }
      } catch (error) {
        console.error("Error fetching widget data:", error);
        setWidgetDataError(String(error));
      } finally {
        setWidgetDataLoading(false);
      }
    };

    fetchWidgetData();
  }, [currencyId, walletId, exchange]);

  const widgetData = {
    user,
    paymentsLimits,
    balance,
    widgetDataLoading,
    widgetDataError,
    alfredIneligibilityCode,
  };

  return <DepositWithdrawContext.Provider value={widgetData}>{children}</DepositWithdrawContext.Provider>;
};

export const useDepositWithdrawContext = (): DepositWithdrawContextType => {
  const context = useContext(DepositWithdrawContext);

  if (context === undefined) {
    throw new Error("useDepositWithdrawContext must be used within a DepositWithdrawProvider");
  }

  return context;
};
